import {
    configure,
    searchBox,
    hits,
    infiniteHits,
    pagination,
    stats,
    clearRefinements,
    currentRefinements,
    sortBy,
    refinementList,
    rangeSlider,
    panel,
} from 'instantsearch.js/es/widgets';
import { getTemplateRenderer } from './template';
import { setupFacets } from './facets';
import { setupSorting } from './sorting';

export function buildWidgets(postType, appsInGroup, config, isSuggestionsGroup, debounceQueryHook) {
    const widgets = [];
    const hitsPerPage = parseInt(config.frontend.per_page, 10) || 12;
    const initialFilter =
        config.frontend.archive_filters && config.frontend.archive_filters[postType]
            ? config.frontend.archive_filters[postType]
            : '';

    widgets.push(
        configure({
            hitsPerPage,
            filters: isSuggestionsGroup ? '' : initialFilter,
        })
    );

    let hasMountedSearchBox = false;

    appsInGroup.forEach((app) => {
        // Search Box
        const searchBoxEl = app.querySelector('.snaf-searchbox');
        if (searchBoxEl) {
            hasMountedSearchBox = true;
            const isSuggestionsApp = app.classList.contains('type-suggestions');
            const scopedPlaceholder = config.frontend.scoped_search_input_placeholder || '';
            const globalPlaceholder = config.frontend.global_search_input_placeholder || '';
            const placeholderText = isSuggestionsApp ? globalPlaceholder : scopedPlaceholder;

            widgets.push(
                searchBox({
                    container: searchBoxEl,
                    placeholder: placeholderText,
                    showSubmit: false,
                    showReset: true,
                    queryHook: debounceQueryHook,
                })
            );
        }

        // Desktop Stats
        const statsEl = app.querySelector('.snaf-stats');
        const mobileStatsEl = app.querySelector('.snaf-mobile-stats');
        const statsTemplate = {
            text(hit, { html }) {
                const suffix = hit.hasManyResults ? 'results' : 'result';
                const q = hit.query && hit.query !== '*' ? hit.query : '';
                return html`<span class="snaf-stats-text"
                    ><strong>${hit.nbHits.toLocaleString()}</strong> ${suffix} found${' '}
                    ${q ? `for "${q}" ` : ''}in ${hit.processingTimeMS}ms</span
                >`;
            },
        };

        if (statsEl) {
            widgets.push(
                stats({
                    container: statsEl,
                    templates: statsTemplate,
                })
            );
        }

        if (mobileStatsEl) {
            widgets.push(
                stats({
                    container: mobileStatsEl,
                    templates: statsTemplate,
                })
            );
        }

        // Hits & Pagination Handling
        const hitsEl = app.querySelector('.snaf-hits');
        const paginationEl = app.querySelector('.snaf-pagination');
        const paginationMode = config.frontend.pagination_style || 'numeric';

        if (hitsEl) {
            const renderer = getTemplateRenderer(postType);
            const emptyTemplate = (results, { html }) => {
                const query = String(results.query || '').trim();
                const archiveTermName = config.frontend.archive_term_name || '';
                const displayTerm = !query || query === '*' ? archiveTermName : query;

                const isUpcPattern = /^\d{12,13}$/.test(query);
                const hasUpcField = (config.schema[postType] || []).some(
                    (f) => f.field_slug === 'upc'
                );

                if (isUpcPattern && hasUpcField) {
                    console.log('UPC Pattern found');
                    return html`
                        <div class="snaf-no-results snaf-no-results--upc">
                            <strong>Searching for a UPC?</strong>
                            <p>
                                We couldn't find an exact match. Try searching for a product name or
                                a different term to see more results.
                            </p>
                        </div>
                    `;
                }

                if (!displayTerm) {
                    return html` <div class="snaf-no-results"><p>No results found.</p></div> `;
                }

                return html`
                    <div class="snaf-no-results">
                        <p>No results found for "<strong>${displayTerm}</strong>".</p>
                    </div>
                `;
            };

            if (paginationMode === 'loadmore' || paginationMode === 'load_more') {
                widgets.push(
                    infiniteHits({
                        container: hitsEl,
                        templates: {
                            item(hit, bindArgs) {
                                return renderer(hit, bindArgs);
                            },
                            empty: emptyTemplate,
                            showMoreText: 'Load More',
                        },
                    })
                );
            } else {
                widgets.push(
                    hits({
                        container: hitsEl,
                        templates: {
                            item(hit, bindArgs) {
                                return renderer(hit, bindArgs);
                            },
                            empty: emptyTemplate,
                        },
                    })
                );
            }
        }

        // Pagination UI
        if (paginationEl && (paginationMode === 'numeric' || paginationMode === 'numbers')) {
            widgets.push(
                pagination({
                    container: paginationEl,
                    showFirst: false,
                    showLast: false,
                    padding: 2,
                })
            );
        }

        // Current Refinements
        const currentRefinementsEl = app.querySelector('.snaf-current-refinements');
        if (currentRefinementsEl) {
            widgets.push(currentRefinements({ container: currentRefinementsEl }));
        }

        // Clear Refinements
        const clearRefinementsEl = app.querySelector('.snaf-clear-refinements');
        if (clearRefinementsEl) {
            widgets.push(
                clearRefinements({
                    container: clearRefinementsEl,
                    templates: {
                        resetLabel({ hasRefinements }, { html }) {
                            return html`<span>Clear all filters</span>`;
                        },
                    },
                })
            );
        }

        // Add Facets
        const facetsEl = app.querySelector('.snaf-facets');
        if (facetsEl) {
            widgets.push(...setupFacets(postType, facetsEl, refinementList, rangeSlider, panel));
        }

        // Add Sorting
        const sortByEl = app.querySelector('.snaf-sort-by');
        if (sortByEl) {
            const sortWidget = setupSorting(postType, sortByEl, sortBy);
            if (sortWidget) widgets.push(sortWidget);
        }
    });

    if (!hasMountedSearchBox) {
        const virtualContainer = document.createElement('div');
        widgets.push(
            searchBox({
                container: virtualContainer,
                showSubmit: false,
                showReset: false,
                queryHook: debounceQueryHook,
            })
        );
    }

    return widgets;
}
